import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, map, mergeMap, switchMap, withLatestFrom } from "rxjs/operators";

import { MpCommonService } from "../../../services/api/mp-common.service";
import { V2AdminService } from "../../../services/api/v2-admin.service";
import { ComplianceRegimeActions } from "../../actions/compliance-regime.actions";
import { FailState } from "../../models/fail-state";
import { AppState } from "../../reducers";
import { ComplianceRegimeSelectors } from "../../selectors/compliance-regime.selectors";

@Injectable()
export class ComplianceRegimeEffects {
	loadRegimes = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.get),
			withLatestFrom(this.store.select(ComplianceRegimeSelectors.state)),
			switchMap(([, state]) =>
				state && state.hasLoaded && !state.loading
					? of(
						ComplianceRegimeActions.regimes.getSuccess({
							regimes: Object.values(state.regimes)
						})
					)
					: of(ComplianceRegimeActions.regimes.getApi())
			)
		)
	);

	loadRegimesApi = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.getApi),
			switchMap(() =>
				this.adminService.listComplianceRegimes().pipe(
					map((regimes) =>
						ComplianceRegimeActions.regimes.getSuccess({ regimes })
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimes.getFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	createRegime = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.create),
			mergeMap(({ request }) =>
				this.adminService.createComplianceRegime(request).pipe(
					map((regime) =>
						ComplianceRegimeActions.regimes.createSuccess({ regime })
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimes.createFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	updateRegime = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.update),
			mergeMap(({ code, payload }) =>
				this.adminService.updateComplianceRegime(code, payload).pipe(
					map((regime) =>
						ComplianceRegimeActions.regimes.updateSuccess({ regime })
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimes.updateFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	disableRegime = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.disable),
			mergeMap(({ code, accountStatusRemarks }) =>
				this.adminService.disableComplianceRegime(code, accountStatusRemarks).pipe(
					map(() => ComplianceRegimeActions.regimes.disableSuccess({ code })),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimes.disableFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	enableRegime = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.enable),
			mergeMap(({ code, accountStatusRemarks }) =>
				this.adminService.enableComplianceRegime(code, accountStatusRemarks).pipe(
					map(() => ComplianceRegimeActions.regimes.enableSuccess({ code })),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimes.enableFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	mapProducts = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.mapProducts),
			mergeMap(({ code, productIds }) =>
				this.adminService.replaceRegimeProducts(code, productIds).pipe(
					map(() =>
						ComplianceRegimeActions.regimes.mapProductsSuccess({
							code,
							productIds
						})
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimes.mapProductsFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	loadRegimeVendors = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.vendors.get),
			withLatestFrom(this.store.select(ComplianceRegimeSelectors.state)),
			switchMap(([{ code }, state]) => {
				const loaded = state?.regimeVendorsLoaded?.[code];
				const cached = state?.regimeVendors?.[code];
				if (loaded && cached) {
					return of(
						ComplianceRegimeActions.regimes.vendors.getSuccess({
							code,
							vendors: cached
						})
					);
				}
				return of(ComplianceRegimeActions.regimes.vendors.getApi({ code }));
			})
		)
	);

	loadRegimeVendorsApi = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.vendors.getApi),
			mergeMap(({ code }) =>
				this.adminService.getRegimeVendors(code).pipe(
					map((vendors) =>
						ComplianceRegimeActions.regimes.vendors.getSuccess({
							code,
							vendors
						})
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimes.vendors.getFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	replaceRegimeVendors = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimes.vendors.replace),
			mergeMap(({ code, vendorIds }) =>
				this.adminService.replaceRegimeVendors(code, vendorIds).pipe(
					map(() =>
						ComplianceRegimeActions.regimes.vendors.replaceSuccess({
							code,
							vendorIds
						})
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimes.vendors.replaceFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	loadVendorRegimes = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.vendorRegimes.get),
			mergeMap(({ vendorId }) =>
				this.adminService.getVendorComplianceRegimes(vendorId).pipe(
					map((restrictions) =>
						ComplianceRegimeActions.vendorRegimes.getSuccess({
							vendorId,
							restrictions
						})
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.vendorRegimes.getFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	assignVendorRegime = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.vendorRegimes.assign),
			mergeMap(({ vendorId, regimeCode }) =>
				this.adminService.assignVendorComplianceRegime(vendorId, regimeCode).pipe(
					map((restriction) =>
						ComplianceRegimeActions.vendorRegimes.assignSuccess({
							vendorId,
							restriction
						})
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.vendorRegimes.assignFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	removeVendorRegime = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.vendorRegimes.remove),
			mergeMap(({ vendorId, regimeCode }) =>
				this.adminService.removeVendorComplianceRegime(vendorId, regimeCode).pipe(
					map(() =>
						ComplianceRegimeActions.vendorRegimes.removeSuccess({
							vendorId,
							regimeCode
						})
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.vendorRegimes.removeFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	loadRegimeMappings = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimeMappings.get),
			withLatestFrom(this.store.select(ComplianceRegimeSelectors.state)),
			switchMap(([, state]) =>
				state && state.mappingsLoaded && state.mappings
					? of(
						ComplianceRegimeActions.regimeMappings.getSuccess({
							mappings: state.mappings
						})
					)
					: of(ComplianceRegimeActions.regimeMappings.getApi())
			)
		)
	);

	loadRegimeMappingsApi = createEffect(() =>
		this.actions.pipe(
			ofType(ComplianceRegimeActions.regimeMappings.getApi),
			switchMap(() =>
				this.commonService.complianceRegimeMappings().pipe(
					map((mappings) =>
						ComplianceRegimeActions.regimeMappings.getSuccess({ mappings })
					),
					catchError((error) =>
						of(
							ComplianceRegimeActions.regimeMappings.getFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private adminService: V2AdminService,
		private commonService: MpCommonService,
		private store: Store<AppState>
	) {}
}
